import React, { forwardRef, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  Avatar,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
  Slide,
} from '@mui/material';
import { Close, Block, ArrowBack, ArrowForward, Person, Category, Groups3, Description } from '@mui/icons-material';
import { variants } from '../../../../variants/variants';
import AttachmentGallery from '../../../../common/Utility/AttachmentGallery ';
import defaultimg from '../../../../common/default.png';

const WinnerDialog = ({ open, onClose, data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollContainerRef = useRef(null);
  const [scrollLen, setScrollLen] = useState(0);

  const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
  });

  const scroll = (direction, click) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: isMobile ? direction * 160 : direction * 300, behavior: 'smooth' });
      setScrollLen(scrollLen + click);
    }
  };

  const teamMembers = data?.winnerTeam?.teamMembers.map((member) => ({
    empCode: member.empCode[0],
    firstName: member.firstName[0],
    lastName: member.lastName[0],
    designation: member.designation[0].trim(),
    department: member.department[0],
    photo: member.photo[0],
  }));

  const renderAttachments = (attachments) => {
    if (attachments?.length > 0) {
      return (
        <Box display="flex" alignItems="center">
          <AttachmentGallery attachments={attachments} isDetailScreen={true} defaultimg={defaultimg} />
        </Box>
      );
    }
    return (
      <Typography
        variant="body2"
        sx={{ color: 'gray', display: 'flex', alignItems: 'center', fontSize: variants.fontSize.SUB_HEADING }}
      >
        <Block sx={{ marginRight: 0.5 }} /> No attachment provided
      </Typography>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      TransitionComponent={isMobile ? Transition : undefined}
      transitionDuration={500}
      keepMounted
      aria-describedby="dialog-slide-description"
    >
      <DialogTitle
        sx={{
          backgroundColor: 'white',
          color: 'black',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: isMobile ? '16px' : '22px',
          fontWeight: '600',
          padding: isMobile ? '10px 14px' : '16px 24px',
          borderBottom: '0.5px dotted lightgrey',
          marginBottom: '1.5rem',
        }}
      >
        {data?.isTeamNomination ? 'Team Details' : 'Winner Details'}
        <IconButton {...(open && { className: 'backButton' })} onClick={onClose} size="small" sx={{ color: 'black' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '15px', height: 'auto', marginBottom: '1rem' }}>
        {data && (
          <>
            <Box display="flex" alignItems="center" mb={2}>
              <Typography component="div" sx={{ marginRight: '6px', fontSize: isMobile ? '14px' : 'inherit' }}>
                Quarter Details :
              </Typography>
              <Chip
                label={`Year: ${data.time.year}`}
                sx={{ marginRight: '6px', fontSize: isMobile ? '12px' : 'inherit' }}
              />
              <Chip label={`Quarter: ${data.time.quarter}`} sx={{ fontSize: isMobile ? '12px' : 'inherit' }} />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" alignItems="center" mb={2}>
              <Person sx={{ marginRight: '6px', color: 'grey' }} />
              <Typography variant="body1" component="span" sx={{ fontSize: isMobile ? '14px' : 'inherit' }}>
                Nominated by
                <Typography component="span" sx={{ marginLeft: '4px', fontSize: isMobile ? '14px' : 'inherit' }}>
                  {data.isTeamNomination
                    ? data.winnerTeam.nominations.nominatorFirstName
                    : data.winner.nominations.nominatorFirstName}
                </Typography>
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" mb={2}>
              <Category sx={{ marginRight: '6px', color: 'grey' }} />
              <Typography variant="body1" component="span" sx={{ fontSize: isMobile ? '14px' : 'inherit' }}>
                Category : {data.categoryName}
              </Typography>
            </Box>
            {data.isTeamNomination && (
              <Box display="flex" alignItems="center" mb={2}>
                <Groups3 sx={{ marginRight: '6px', color: 'grey' }} />
                <Typography
                  variant="body1"
                  component="div"
                  sx={{ marginRight: '6px', fontSize: isMobile ? '14px' : 'inherit' }}
                >
                  Team Members :{' '}
                  {teamMembers?.map((member, index) => (
                    <Chip
                      key={index}
                      label={member.firstName}
                      sx={{
                        marginRight: '6px',
                        marginBottom: '10px',
                        cursor: 'default',
                        '&:hover': { backgroundColor: '#e0e0e0' },
                        fontSize: isMobile ? '12px' : 'inherit',
                      }}
                    />
                  ))}
                </Typography>
              </Box>
            )}
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" alignItems="start" mb={2}>
              <Description sx={{ marginRight: '6px', color: 'grey' }} />
              <Typography variant="body1" component="span" sx={{ fontSize: isMobile ? '14px' : 'inherit' }}>
                <b>Nomination Reason :</b>{' '}
                {data.isTeamNomination ? data.winnerTeam.nominations.reason : data.winner.nominations.reason}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box mt={2}>
              <Typography variant="body1" sx={{ fontSize: isMobile ? '14px' : 'inherit' }}>
                Attachment :
              </Typography>
              {renderAttachments(
                data.isTeamNomination ? data.winnerTeam.nominations.attachments : data.winner.nominations.attachments
              )}
            </Box>
            <Typography variant="body2" sx={{ color: 'grey', mt: 2, fontSize: isMobile ? '12px' : 'inherit' }}>
              Nominated on:{' '}
              {new Date(
                data.isTeamNomination
                  ? data.winnerTeam.nominations.dateNominated
                  : data.winner.nominations.dateNominated
              ).toLocaleString()}
            </Typography>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WinnerDialog;
